import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { KeyRound, MonitorSmartphone, Smartphone } from 'lucide-react'
import { LogoMark } from '@/components/generals/logo'
import SectionHeading from '@/components/landing/section-heading'

const POINTS = [
    {
        icon: Smartphone,
        title: "No email needed",
        description: "Easy for counter, warehouse and field staff who don't use email at work."
    },
    {
        icon: KeyRound,
        title: "A PIN for every person",
        description: "Everyone gets a sign-in of their own, so each one has a name on it."
    },
    {
        icon: MonitorSmartphone,
        title: "Works on any device",
        description: "Elenix runs in the browser on phones, tablets and computers. Nothing to install."
    }
]

const PhoneMock: FC = (): ReactNode => {
    return (
        <div className='rounded-[2.4rem] bg-brand-950 p-2 shadow-[0_40px_70px_-30px_rgb(11_18_48/0.7)] ring-1 ring-white/20'>
            <div className='overflow-hidden rounded-[2rem] bg-background px-4 pb-5 pt-3'>
                <div className='mx-auto h-1.5 w-14 rounded-full bg-black/10' />

                <LogoMark className='mt-6 size-11 rounded-2xl text-lg' />

                <p className='mt-4 font-heading text-lg font-bold tracking-tight text-foreground'>
                    Welcome to Elenix
                </p>

                <p className='mt-1 text-xs text-muted-foreground'>
                    Sign in with your mobile number and PIN.
                </p>

                <div className='mt-4 overflow-hidden rounded-2xl bg-white ring-1 ring-border'>
                    <div className='px-3.5 py-2.5'>
                        <p className='text-[10px] font-semibold text-brand-600'>
                            Mobile number
                        </p>

                        <p className='mt-0.5 text-sm text-foreground tabular-nums'>
                            98765 43210
                        </p>
                    </div>

                    <div className='border-t border-border px-3.5 py-2.5'>
                        <p className='text-[10px] font-semibold text-brand-600'>
                            PIN
                        </p>

                        <div className='mt-2 flex gap-1.5 pb-0.5'>
                            <span className='size-2 rounded-full bg-foreground' />
                            <span className='size-2 rounded-full bg-foreground' />
                            <span className='size-2 rounded-full bg-foreground' />
                            <span className='size-2 rounded-full bg-foreground' />
                        </div>
                    </div>
                </div>

                <div className='mt-3 grid h-10 place-items-center rounded-full bg-primary text-sm font-semibold text-primary-foreground'>
                    Sign in
                </div>
            </div>
        </div>
    )
}

const PhoneSignInSection: FC = (): ReactNode => {
    return (
        <section id='phone-sign-in' aria-labelledby='phone-title' className='overflow-hidden bg-white py-20 sm:py-28'>
            <div className='mx-auto grid max-w-6xl items-center gap-14 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8'>
                <div>
                    <SectionHeading
                        id='phone-title'
                        eyebrow="Phone-first sign-in"
                        title="Your team signs in with a phone number and a PIN."
                        description="No email addresses to set up and no passwords to forget. Everyone gets a PIN of their own, so you always know who signed in."
                    />

                    <ul className='mt-10 space-y-6'>
                        {POINTS.map(point => (
                            <li key={point.title} className='reveal flex gap-4'>
                                <span className='grid size-11 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100'>
                                    <point.icon className='size-5' />
                                </span>

                                <div>
                                    <h3 className='font-heading text-base font-bold text-foreground'>
                                        {point.title}
                                    </h3>

                                    <p className='mt-1 text-base leading-relaxed text-muted-foreground'>
                                        {point.description}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='reveal relative pb-12 sm:pb-16'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-muted sm:aspect-[4/3] lg:aspect-[4/5]'>
                        <Image
                            src='/images/phone-sign-in.jpg'
                            alt="A team member checking his phone at work"
                            fill
                            sizes='(min-width: 1024px) 560px, 100vw'
                            className='object-cover object-[45%_center]'
                        />

                        <div aria-hidden='true' className='absolute inset-0 bg-linear-to-t from-brand-950/35 via-transparent to-transparent' />
                    </div>

                    <div aria-hidden='true' className='absolute bottom-0 left-4 w-[13.5rem] sm:left-8 sm:w-[15.5rem] lg:-left-8'>
                        <PhoneMock />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default PhoneSignInSection
